'use client';

import { Fragment, useId, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Checkbox } from '@savvy-kit/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { Separator } from '@savvy-kit/components/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@savvy-kit/components/tabs';
import { DatePicker } from '@savvy-kit/patterns/date-picker';
import { SavedViews, SaveViewDialog } from '@savvy-kit/patterns/saved-views';
import { isNil } from 'es-toolkit';
import { Plus } from 'lucide-react';

import type { ExportableColumn, ExportFormat } from '../../types/export';
import type { ExportDateRangeSlotProps, ExportModalSlotProps } from '../../types/export-slots';
import { EXPORT_FORMAT_LABELS } from '../../constants/export';
import { SortableColumnChips } from './sortable-column-chips';

type ColumnGroup = {
  readonly group: string | undefined;
  readonly columns: ExportableColumn[];
};

// The pickers live inside a Dialog, whose backdrop would otherwise cover the calendar popover.
const DATE_POPOVER_Z = 'z-[1400]';

function ExportDateRange({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate,
}: ExportDateRangeSlotProps) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Date range</p>
      <div className="flex flex-wrap items-center gap-2">
        <DatePicker
          value={startDate}
          onChange={onStartDateChange}
          clearable
          ariaLabel="Export start date"
          placeholder="Earliest"
          minDate={minDate}
          maxDate={endDate ?? maxDate}
          triggerClassName="h-8 w-[150px] text-[13px]"
          popoverPositionerClassName={DATE_POPOVER_Z}
        />
        <span className="text-sm text-muted-foreground">to</span>
        <DatePicker
          value={endDate}
          onChange={onEndDateChange}
          clearable
          ariaLabel="Export end date"
          placeholder="Latest"
          minDate={startDate ?? minDate}
          maxDate={maxDate}
          triggerClassName="h-8 w-[150px] text-[13px]"
          popoverPositionerClassName={DATE_POPOVER_Z}
        />
      </div>
      <p className="text-sm text-muted-foreground">Both days are included. Leave either empty for no bound.</p>
    </div>
  );
}

// Ungrouped columns render flat at the top (backwards compat for callers that
// don't set `group`), followed by defined groups in first-appearance order.
function groupColumnsForPicker(columns: readonly ExportableColumn[]): ColumnGroup[] {
  const ungrouped = columns.filter((column) => isNil(column.group));
  const groupOrder: string[] = [];
  for (const column of columns) {
    if (!isNil(column.group) && !groupOrder.includes(column.group)) {
      groupOrder.push(column.group);
    }
  }

  const groups = groupOrder.map((group) => ({
    group,
    columns: columns.filter((column) => column.group === group),
  }));

  return ungrouped.length > 0 ? [{ group: undefined, columns: ungrouped }, ...groups] : groups;
}

export type DataTableExportModalProps = ExportModalSlotProps;

export function DataTableExportModal({
  open,
  onOpenChange,
  formats,
  selectedFormat,
  state,
  availableColumns,
  selectedColumns,
  canExport,
  setSelectedFormat,
  addColumn,
  removeColumn,
  reorderColumns,
  execute,
  filterScope,
  savedViews,
  rowCountLabel,
  dateRange,
}: DataTableExportModalProps) {
  const filterScopeCheckboxId = useId();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const handleExport = async () => {
    try {
      await execute();
      onOpenChange(false);
    } catch {
      // Promise rejected - error handling is managed by the execute function and state
      // Modal remains open to show error state
    }
  };

  const showTabs = formats.length > 1;

  const renderColumnPicker = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">Display properties</p>
        <p className="text-sm text-muted-foreground">Select the display properties that you want to export</p>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="secondary" size="sm" icon={<Plus />} />}>
          Display properties
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {availableColumns.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">All columns selected</div>
          ) : (
            groupColumnsForPicker(availableColumns).map(({ group, columns }, index) => (
              <Fragment key={group ?? 'ungrouped'}>
                {index > 0 && <DropdownMenuSeparator />}
                {!isNil(group) && <DropdownMenuLabel>{group}</DropdownMenuLabel>}
                {columns.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={false}
                    onCheckedChange={() => addColumn(column.id)}
                  >
                    {column.header}
                  </DropdownMenuCheckboxItem>
                ))}
              </Fragment>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <SortableColumnChips
        columns={selectedColumns}
        removable={selectedColumns.length > 1}
        onRemove={removeColumn}
        onReorder={reorderColumns}
      />

      {!isNil(state.error) && <p className="text-sm text-destructive">{state.error}</p>}
    </div>
  );

  const renderContent = () => {
    if (!showTabs) {
      return renderColumnPicker();
    }

    return (
      <Tabs value={selectedFormat} onValueChange={(value) => setSelectedFormat(value as ExportFormat)}>
        <TabsList className="self-center">
          {formats.map((format) => (
            <TabsTrigger key={format} value={format} className="p-2">
              {EXPORT_FORMAT_LABELS[format]}
            </TabsTrigger>
          ))}
        </TabsList>
        {formats.map((format) => (
          <TabsContent key={format} value={format} className="mt-4">
            {renderColumnPicker()}
          </TabsContent>
        ))}
      </Tabs>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export as {EXPORT_FORMAT_LABELS[selectedFormat]}</DialogTitle>
          <DialogDescription>
            {isNil(filterScope)
              ? 'All data will be exported using the selected filters. You can include additional display fields if needed.'
              : 'Choose the display fields to include. Use the toggle below to control whether your current filters apply to the export.'}
          </DialogDescription>
        </DialogHeader>

        <Separator />

        {!isNil(savedViews) && (
          <>
            <div className="flex items-center justify-between gap-2">
              <SavedViews
                savedViews={[...savedViews.views]}
                activeViewId={savedViews.activeViewId}
                isModified={savedViews.isModified}
                onLoadView={(view) => savedViews.onLoadView(view.id)}
                onClearView={savedViews.onClearView}
                onRenameView={savedViews.onRenameView}
                onDeleteView={savedViews.onDeleteView}
                renameDialogTitle="Rename preset"
              />
              {!savedViews.atLimit && (
                <Button variant="secondary" size="sm" onClick={() => setSaveDialogOpen(true)}>
                  Save as preset
                </Button>
              )}
            </div>

            <SaveViewDialog
              open={saveDialogOpen}
              onOpenChange={setSaveDialogOpen}
              existingNames={savedViews.views.map((view) => view.name)}
              onSubmit={savedViews.onSaveView}
              labels={{ title: 'Save export as preset', placeholder: 'e.g. Q1 export' }}
            />
          </>
        )}

        {renderContent()}

        {/* Outside the format tabs on purpose: the range applies to both, and rendering it per tab
            would duplicate the pickers. */}
        {!isNil(dateRange) && <ExportDateRange {...dateRange} />}

        {!isNil(filterScope) && (
          <div className="space-y-1">
            <Checkbox
              id={filterScopeCheckboxId}
              checked={filterScope.checked}
              onCheckedChange={(next) => filterScope.onCheckedChange(next === true)}
              label={filterScope.label}
              description={filterScope.helperText}
            />
          </div>
        )}

        <DialogFooter>
          {!isNil(rowCountLabel) && rowCountLabel !== '' && (
            <p className="mr-auto self-center text-sm text-muted-foreground">{rowCountLabel}</p>
          )}
          <Button onClick={handleExport} disabled={!canExport} isLoading={state.status === 'exporting'}>
            {`Export as ${EXPORT_FORMAT_LABELS[selectedFormat]}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
